import { StaticJsonRpcProvider } from '@ethersproject/providers'
import { BigNumber } from 'ethers'
import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getFeeDistributorContract } from '~/contracts/getFeeDistributorContract'
import { getVotingEscrowContract } from '~/contracts/getVotingEscrowContract'
import { FeeDistributor, VotingEscrow } from '~/contracts/types'
import { useVESISParams } from '~/hooks/useVESISParams'
import { SECONDS_IN_WEEK } from '~/lib/constants'
import { getWeeklySISDistribution } from '~/lib/getEstimateWeaklyRewards'
import { getNextRewardAt } from '~/lib/getNextRewardAt'
import { sisLockKeys } from '~/lib/queryKeys'
import { SimpleFraction } from '~/lib/SimpleFraction'
import { useWallet } from '~/lib/useWallet'
import { VESISConfig } from '~/types/config'
import { getGraphUserInfo } from './getGraphUserInfo'

export interface Lock {
	amount: BigNumber
	end: BigNumber
	rewards: BigNumber
	availableToClaim: BigNumber
	estimateWeaklyRewards: BigNumber
	totalRewards: BigNumber
	currentVeBalance: BigNumber
	nextRewardAt: BigNumber
	createdAt?: BigNumber
	lastClaimedAt?: BigNumber
}

interface GetEstimateWeaklyRewardsParams {
	votingEscrowContract: VotingEscrow
	feeDistributorContract: FeeDistributor
	account: string
	config: VESISConfig
	provider: StaticJsonRpcProvider
}

async function getEstimateWeaklyRewards({
	votingEscrowContract,
	feeDistributorContract,
	account,
	config,
	provider,
}: GetEstimateWeaklyRewardsParams): Promise<SimpleFraction> {
	const veSISBalance = await votingEscrowContract['balanceOf(address)'](
		account
	)
	const totalVESISBalance = await votingEscrowContract['totalSupply()']()

	const weaklySISDistribution = await getWeeklySISDistribution(
		config,
		provider,
		feeDistributorContract
	)

	const share = new SimpleFraction(veSISBalance).div(totalVESISBalance)

	return share.mul(weaklySISDistribution)
}

export function useLockDetails(onSuccess?: () => void) {
	const { config, provider } = useVESISParams()

	const { account } = useWallet()

	const result = useQuery<Lock | null>({
		queryKey: sisLockKeys.details(config.chain.id, account),
		queryFn: async () => {
			const votingEscrowContract = getVotingEscrowContract(
				config,
				provider
			)
			const feeDistributorContract = getFeeDistributorContract(
				config,
				provider
			)

			const { amount, end } = await votingEscrowContract.locked(account)
			if (amount.isZero()) {
				return null
			}

			const user = await getGraphUserInfo(config, account)

			const rewards = BigNumber.from(user?.totalRewards ?? 0)

			let availableToClaim: BigNumber
			try {
				availableToClaim =
					await feeDistributorContract.callStatic.claim({ from: account })
			} catch {
				availableToClaim = BigNumber.from(0)
			}

			const nextRewardAt = getNextRewardAt(config)

			// const token = new Token({
			// 	symbol: 'SIS',
			// 	decimals: 18,
			// 	address: '0x0000000000000000000000000000000000000000',
			// 	chainId: 1
			// })
			// const data: any = []
			// for(let i = 0; i < 20; i++ ) {
			//
			// 	const at = nextRewardAt.sub(i * config.week)
			// 	const reward = await feeDistributorContract.tokens_per_week(at)
			// 	data.push({
			// 		at: (new Date(at.toNumber() * 1000)).toDateString(),
			// 		reward: new TokenAmount(token, reward.toString()).toSignificant()
			// 	})
			// }
			// console.log(data)

			const currentVeBalance = await votingEscrowContract[
				'balanceOf(address)'
			](account)

			const estimateWeaklyRewards = await getEstimateWeaklyRewards({
				config,
				provider,
				votingEscrowContract,
				feeDistributorContract,
				account,
			})

			const remainingWeeks = new SimpleFraction(
				Math.floor(end.toNumber() - Date.now() / 1000),
				SECONDS_IN_WEEK
			)

			const totalRewards = estimateWeaklyRewards.mul(
				remainingWeeks.toBigNumber()
			)

			return {
				amount,
				availableToClaim,
				end,
				estimateWeaklyRewards: estimateWeaklyRewards.toBigNumber(),
				rewards,
				totalRewards: totalRewards.toBigNumber(),
				currentVeBalance,
				nextRewardAt,
				createdAt: user?.lastLockTime,
				lastClaimedAt: user?.lastClaimedAt,
			}
		},
		enabled: !!account,
	})

	useEffect(() => {
		if (result.data !== undefined && onSuccess) {
			onSuccess()
		}
	}, [result.data])

	return result
}
